import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { conversionCount, num, withDeviceDisplay } from '../format';
import { useReportWidget } from '../hooks/useReportWidget';
import { COLORS, cardSx } from '../theme';
import { googleAdsRows } from './GoogleAdsCampaignBreakdownCard';
import WidgetSkeleton from './WidgetSkeleton';

const KEY = 'googleAdsDeviceConversions';

/**
 * flank#4010 — Google's OTHER has no device row (NULL), so name it here; the
 * shared relabelling (TV → CTV, flank#4882) keeps this card in line with the
 * rest of the page.
 */
export const googleAdsDeviceLabel = withDeviceDisplay(value => (value ? String(value) : 'Other'));

const GoogleAdsDeviceConversionsCard = () => {
  const {
    rows: fetchedRows,
    loading,
    error,
  } = useReportWidget(KEY, {
    dimensions: ['dsp', 'device'],
    metrics: ['clicks', 'conversions'],
    sort: [{ field: 'clicks', dir: 'desc' }],
    limit: 50,
    dspsOverride: ['googleads'],
  });
  const rows = googleAdsRows(fetchedRows);

  return (
    <Box sx={{ ...cardSx, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant='subtitle1' sx={{ fontWeight: 600, color: COLORS.text }}>
        Google Ads — All Conversions by Device
      </Typography>

      {loading ? (
        <WidgetSkeleton height={200} />
      ) : error ? (
        <Typography variant='body2' color='error'>
          {error}
        </Typography>
      ) : rows.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          No Google Ads device data for the selected range yet.
        </Typography>
      ) : (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: COLORS.muted, fontWeight: 600 }}>Device</TableCell>
              <TableCell align='right' sx={{ color: COLORS.muted, fontWeight: 600 }}>
                Clicks
              </TableCell>
              <TableCell align='right' sx={{ color: COLORS.muted, fontWeight: 600 }}>
                All Conversions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: ReportRow) => (
              <TableRow key={googleAdsDeviceLabel(row.device)}>
                <TableCell>{googleAdsDeviceLabel(row.device)}</TableCell>
                <TableCell align='right'>{num(row.clicks)}</TableCell>
                <TableCell align='right'>{conversionCount(row.conversions)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default GoogleAdsDeviceConversionsCard;
